import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProjectService } from '../../../../shared/services/project.service';
import { ProjectStage } from '../../../../shared/models/project.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PreventScrollPropagationDirective } from '../../../../shared/directives/prevent-scroll-propagation.directive';

function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-edit-stage-project',
  imports: [FormsModule, CommonModule, DateInputsModule, PreventScrollPropagationDirective],
  templateUrl: './edit-stage-project.html'
})
export class EditStageProjectComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Input({ required: true }) stage!: ProjectStage;
  @Input() projectStartDate?: string;
  @Input() projectBudget: number = 0;
  @Input() existingPhaseHours: number = 0; // total of OTHER phases (excluding this one)

  private projectService = inject(ProjectService);
  isSubmitting = false;
  statusList = ["NOTBEGIN", "INPROGRESS", "DONE"];

  // Form data
  stageName = '';
  color = '#6366f1';
  status: "NOTBEGIN" | "INPROGRESS" | "DONE" = 'NOTBEGIN';
  estimatedTimeInput: string | number = '';

  // Date objects for Kendo
  minDate: Date = new Date();
  startDateValue: Date = new Date();
  endDateValue: Date = new Date();
  startDate = '';
  endDate = '';

  statusOptions = [
    { text: 'New', value: 'NOTBEGIN' },
    { text: 'In Progress', value: 'INPROGRESS' },
    { text: 'Done', value: 'DONE' }
  ];

  ngOnInit() {
    // Pre-fill from input stage
    this.stageName = this.stage.stageName || '';
    this.color = this.stage.color || '#6366f1';
    this.status = (this.stage.status as "NOTBEGIN" | "INPROGRESS" | "DONE") || 'NOTBEGIN';
    this.estimatedTimeInput = this.stage.estimatedHours || '';

    // Parse dates
    if (this.stage.startDate) {
      this.startDateValue = new Date(this.stage.startDate + 'T00:00:00');
      this.startDate = this.stage.startDate;
    }
    if (this.stage.endDate) {
      this.endDateValue = new Date(this.stage.endDate + 'T00:00:00');
      this.endDate = this.stage.endDate;
    }

    // Min date from project
    if (this.projectStartDate) {
      const parts = this.projectStartDate.split('-');
      this.minDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      this.minDate.setHours(0, 0, 0, 0);
    }
  }

  getParsedHours(): number {
    if (!this.estimatedTimeInput) return 0;
    const val = parseFloat(String(this.estimatedTimeInput).replace(',', '.'));
    return isNaN(val) ? 0 : val;
  }

  isOverBudget(): boolean {
    if (!this.projectBudget || this.projectBudget <= 0) return false;
    return (this.existingPhaseHours + this.getParsedHours()) > this.projectBudget;
  }

  getRemainingHours(): number {
    return this.projectBudget - this.existingPhaseHours - this.getParsedHours();
  }

  onStartDateChange(value: Date | null) {
    if (value) {
      this.startDateValue = value;
      this.startDate = formatDateLocal(value);
      if (this.endDateValue < value) {
        this.endDateValue = new Date(value);
        this.endDateValue.setDate(this.endDateValue.getDate() + 30);
        this.endDate = formatDateLocal(this.endDateValue);
      }
    }
  }

  onEndDateChange(value: Date | null) {
    if (value) {
      this.endDateValue = value;
      this.endDate = formatDateLocal(value);
    }
  }

  getStatusText(value: string | null): string {
    const s = this.statusOptions.find(o => o.value === value);
    return s ? s.text : '';
  }

  async onSubmit() {
    if (!this.stageName.trim()) {
      alert('Please enter a phase name.');
      return;
    }
    this.isSubmitting = true;
    try {
      const parsedHours = this.getParsedHours();

      await this.projectService.updateProjectStage(this.stage.projectStageId!, {
        stageName: this.stageName.trim(),
        color: this.color,
        status: this.status,
        startDate: this.startDate,
        endDate: this.endDate,
        estimatedHours: parsedHours
      });

      this.close.emit();
    } catch (err: any) {
      console.error('Error updating stage:', err);
      alert(err?.message || 'Error while updating phase.');
    } finally {
      this.isSubmitting = false;
    }
  }
}
